import { householdUnits } from "./intake";
import type { FoodItem, KayaState } from "./types";

/**
 * A meal typed as one line — "2 roti, 1 katori dal, chai" — read into foods and portions
 * (ADR-048 §2; research market-gaps.md §3.2, §5 #2).
 *
 * Deterministic on purpose: no model, no network, the same line always reads the same way. The
 * line is split into parts; each part becomes a count, a household unit and a name; the name is
 * looked up with the picker's own exact search (passed in as `find`, which is
 * `lib/foodDb.ts::orderMatches` over the table — so every alias the table knows is known here);
 * and the portion comes from the typed count and unit in the food's own household unit, or, with
 * no count, from the portion last logged of that food.
 *
 * A part that finds nothing is returned unmatched and shown as typed. It is never guessed at:
 * fuzzy matching would find *something* for "papad", and a wrong match that looks confident is
 * the one failure the research warns about. Nothing is logged until every row has been seen.
 */

export interface MealPart {
  /** the part as typed, trimmed: "1 katori dal" */
  text: string;
  /** null when no count was typed */
  count: number | null;
  /** a household unit, normalised ("katori", "g", "tablespoon"), or null */
  unit: string | null;
  /** what is left to look up: "dal" */
  name: string;
}

/** The portion of a food last logged, read from the log. */
export interface LastPortion {
  qty: number;
  unit: string;
  /** when it was logged — the most recent food wins a tie between matches */
  at: number;
}

export interface ReadRow {
  part: MealPart;
  /** null when nothing in the table goes by that name */
  item: FoodItem | null;
  qty: number;
  unit: string;
  /**
   * Why this food and this amount: `recent` — the food was chosen because it is one the person
   * logs; `last` — no count was typed, so the portion last logged is used; `serving` — no count
   * and never logged, one of its own unit; `typed` — as typed.
   */
  how: "typed" | "recent" | "last" | "serving";
  /** set when the typed unit is not one this food is measured in, so its own was used */
  unitSwap?: string;
}

/* ---------- splitting ---------- */

// "or" is not a separator — "2 roti or 3" is not two foods. Only these join foods:
const JOINERS = /\s*(?:[,;+&\n]|\s+and\s+|\s+aur\s+|\s+और\s+)\s*/i;

/** Devanagari digits read as ASCII, so "२ रोटी" counts like "2 roti". */
function asciiDigits(s: string): string {
  return s.replace(/[०-९]/g, (c) => String(c.charCodeAt(0) - 0x0966));
}

export function splitMeal(line: string): string[] {
  return asciiDigits(line)
    .split(JOINERS)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

/* ---------- counts and units ---------- */

const WORD_COUNTS: Readonly<Record<string, number>> = {
  // English
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  half: 0.5, quarter: 0.25,
  // Hinglish, as people type it
  ek: 1, do: 2, teen: 3, tin: 3, char: 4, chaar: 4, paanch: 5, panch: 5, chhe: 6, che: 6, saat: 7,
  aath: 8, nau: 9, das: 10, aadha: 0.5, adha: 0.5, aadhi: 0.5, adhi: 0.5, dedh: 1.5, dhai: 2.5,
  sawa: 1.25, pauna: 0.75,
  // Devanagari
  एक: 1, दो: 2, तीन: 3, चार: 4, पांच: 5, पाँच: 5, आधा: 0.5, आधी: 0.5, डेढ़: 1.5, ढाई: 2.5,
};

const FRACTIONS: Readonly<Record<string, number>> = { "½": 0.5, "¼": 0.25, "¾": 0.75, "⅓": 1 / 3, "⅔": 2 / 3 };

/** A key of one of the word maps — never an inherited one ("constructor" is not a unit). */
function lookup<V>(map: Readonly<Record<string, V>>, key: string | undefined): V | undefined {
  const k = (key ?? "").toLowerCase();
  return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : undefined;
}

/**
 * A count token — "2", "1.5", "1/2", "½", "1½", "two", "do" — or null. `words` is false for a
 * count after the name: "roti 2" is two roti, but "vitamin a" is not one vitamin.
 */
function readCount(tok: string, words = true): number | null {
  const t = tok.toLowerCase();
  const w = words ? lookup(WORD_COUNTS, t) : undefined;
  if (w !== undefined) return w;
  const mixed = t.match(/^(\d+)([½¼¾⅓⅔])$/);
  if (mixed) return Number(mixed[1]) + FRACTIONS[mixed[2]];
  const f = lookup(FRACTIONS, t);
  if (f !== undefined) return f;
  const frac = t.match(/^(\d+)\/(\d+)$/);
  if (frac && Number(frac[2]) > 0) return Number(frac[1]) / Number(frac[2]);
  const x = t.match(/^x?(\d+(?:\.\d+)?)x?$/);
  if (x) return Number(x[1]);
  return null;
}

/** Household units as people write them, to one spelling each. The value is what a food row's
 *  own unit is compared against. */
const UNIT_WORDS: Readonly<Record<string, string>> = {
  g: "g", gm: "g", gms: "g", gram: "g", grams: "g", gr: "g", ml: "g", // a drink's ml read as grams, as the table does
  katori: "katori", katoris: "katori", katoree: "katori", kathori: "katori", कटोरी: "katori",
  bowl: "bowl", bowls: "bowl",
  cup: "cup", cups: "cup", mug: "cup", mugs: "cup", कप: "cup",
  glass: "glass", glasses: "glass", gilas: "glass", gilaas: "glass", गिलास: "glass",
  plate: "plate", plates: "plate", थाली: "plate",
  piece: "piece", pieces: "piece", pc: "piece", pcs: "piece", nos: "piece", no: "piece",
  slice: "slice", slices: "slice",
  tsp: "teaspoon", teaspoon: "teaspoon", teaspoons: "teaspoon", chammach: "teaspoon", chamach: "teaspoon", चम्मच: "teaspoon",
  tbsp: "tablespoon", tablespoon: "tablespoon", tablespoons: "tablespoon",
  spoon: "teaspoon", spoons: "teaspoon",
};

/** "3 tomatoes" → "tomato", "katoris" → "katori": the forms a plural takes in a typed line. */
export function singular(word: string): string {
  const w = word.toLowerCase();
  if (w.length <= 3) return w;
  if (/(?:oes|ches|shes|sses|xes)$/.test(w)) return w.slice(0, -2);
  if (/ies$/.test(w)) return `${w.slice(0, -3)}y`;
  if (/s$/.test(w) && !/ss$/.test(w)) return w.slice(0, -1);
  return w;
}

/**
 * One part of the line: a count, a unit, a name, in the orders people type them — "2 roti",
 * "roti 2", "2x roti", "1 katori dal", "100g paneer", "half katori rice", "dal 1 katori". Null
 * when nothing is left to look up.
 */
export function parsePart(raw: string): MealPart | null {
  const text = raw.trim().replace(/\s+/g, " ");
  if (!text) return null;
  // "100g" and "2x" are one token each; split the number from the letters so both read.
  const toks = asciiDigits(text)
    .replace(/^(\d+(?:\.\d+)?)(g|gm|gms|ml|x)\b/i, "$1 $2")
    .split(" ")
    .filter(Boolean);

  let count: number | null = null;
  let unit: string | null = null;
  const takeUnit = (i: number): boolean => {
    const u = lookup(UNIT_WORDS, toks[i]);
    if (!u) return false;
    unit = u;
    toks.splice(i, 1);
    // "katori of dal", "cup of chai"
    if (toks[i]?.toLowerCase() === "of") toks.splice(i, 1);
    return true;
  };

  const lead = toks.length > 1 ? readCount(toks[0]) : null;
  if (lead !== null) {
    count = lead;
    toks.splice(0, 1);
    if (toks[0]?.toLowerCase() === "x") toks.splice(0, 1);
    takeUnit(0);
  } else if (toks.length > 1) {
    // a trailing count, with or without its unit: "roti 2", "dal 1 katori", "chai x2"
    const last = toks.length - 1;
    const tail = readCount(toks[last], false);
    if (tail !== null) {
      count = tail;
      toks.splice(last, 1);
      if (toks[toks.length - 1]?.toLowerCase() === "x") toks.pop();
    } else if (lookup(UNIT_WORDS, toks[last]) && toks.length > 2) {
      const n = readCount(toks[last - 1], false);
      if (n !== null) {
        count = n;
        takeUnit(last);
        toks.splice(last - 1, 1);
      }
    }
  }
  const name = toks.join(" ").trim();
  if (!name) return null;
  return { text, count, unit, name };
}

/**
 * Whether a query reads as a meal rather than a search: more than one part, or one part that
 * starts with a count ("2 roti"). "dal makhani" is a search; "dal, rice" and "2 roti" are meals.
 */
export function looksLikeMeal(q: string): boolean {
  const parts = splitMeal(q);
  if (parts.length > 1) return true;
  const one = parts[0] ? parsePart(parts[0]) : null;
  return !!one && one.count !== null && one.name.length >= 2;
}

/* ---------- the person's own history ---------- */

/**
 * The portion each food was last logged at, by code, from the food log itself — not a separate
 * store. The log already holds every portion, synced and merged by id on every phone (ADR-026),
 * so remembering it again in the profile would be a second copy to drift, and a per-phone copy
 * would forget on the parents' second phone. Tombstoned entries are skipped. (ADR-048)
 */
export function lastPortions(S: KayaState): Map<string, LastPortion> {
  const out = new Map<string, LastPortion>();
  for (const L of Object.values(S.logs)) {
    for (const e of L.food || []) {
      if (!e.code || (L.foodOff && e.id in L.foodOff)) continue;
      const cur = out.get(e.code);
      if (!cur || e.at > cur.at) out.set(e.code, { qty: e.qty, unit: e.unit, at: e.at });
    }
  }
  return out;
}

/* ---------- reading a line ---------- */

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The query as a whole word of the name or an alias: "dal" in "Dal / lentils", not in "Dalia". */
function wholeWord(item: FoodItem, q: string): boolean {
  const re = new RegExp(`(^|[^\\p{L}])${escapeRe(q)}($|[^\\p{L}])`, "iu");
  return re.test(item.n) || (!!item.a && re.test(item.a));
}

/** The food row's own unit compared with a typed one: "tea cup" is a cup, "chapati" is not a
 *  katori. */
function unitMatches(item: FoodItem, unit: string): boolean {
  const own = singular(item.u.toLowerCase());
  return own === unit || own.split(" ").includes(unit);
}

function pick(candidates: FoodItem[], q: string, unit: string | null, last: Map<string, LastPortion>): {
  item: FoodItem;
  recent: boolean;
} {
  // The person's own foods first: of the matches that carry the word whole, the one logged most
  // recently. "dal" matches forty rows; the one she logged at lunch is the one she means.
  let best: { item: FoodItem; at: number } | null = null;
  for (const f of candidates) {
    const p = last.get(f.i);
    if (p && wholeWord(f, q) && (!best || p.at > best.at)) best = { item: f, at: p.at };
  }
  if (best) return { item: best.item, recent: true };
  // Then a row the word names outright — "roti" is "Chapati/Roti", not "Soya roti", which the
  // search ranks first for being shorter — measured in the unit typed where one is; then any row
  // with the word whole in the unit typed; then the search's own order.
  const inUnit = (f: FoodItem) => !!unit && unit !== "g" && unitMatches(f, unit);
  const named = candidates.filter((f) => namedBy(f, q));
  const whole = candidates.filter((f) => wholeWord(f, q));
  const choice = named.find(inUnit) ?? named[0] ?? whole.find(inUnit) ?? candidates[0];
  return { item: choice, recent: false };
}

/** The name split where a table name lists its alternatives — "Chapati/Roti", "Curd / dahi
 *  (whole milk)", "Egg, boiled" — has the query as one whole piece. */
function namedBy(item: FoodItem, q: string): boolean {
  const pieces = item.n
    .toLowerCase()
    .split(/\s*(?:\/|\(|\)|,|\bor\b)\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
  return pieces.includes(q.toLowerCase());
}

/**
 * Reads a typed line into rows. `find` is the exact search over the table the picker shows (the
 * user's own foods included); `last` is `lastPortions` of the log.
 */
export function readMeal(
  line: string,
  find: (q: string) => FoodItem[],
  last: Map<string, LastPortion>
): ReadRow[] {
  const rows: ReadRow[] = [];
  for (const raw of splitMeal(line)) {
    const part = parsePart(raw);
    if (!part) continue;
    // The singular first: "2 eggs" means eggs, and "eggs" alone finds only "Baked eggs in tomato
    // sauce". A name the singular breaks ("chips" → "chip") still finds its row by prefix.
    const tries = [...new Set([singular(part.name), part.name.toLowerCase()])];
    // The first spelling that finds the word whole wins; failing that, the first that finds anything.
    let q = tries[0];
    let found: FoodItem[] = [];
    for (const t of tries) {
      if (t.length < 2) continue;
      const got = find(t);
      if (got.some((f) => wholeWord(f, t))) {
        found = got;
        q = t;
        break;
      }
      if (!found.length && got.length) {
        found = got;
        q = t;
      }
    }
    if (!found.length) {
      rows.push({ part, item: null, qty: part.count ?? 1, unit: part.unit ?? "", how: "typed" });
      continue;
    }
    const { item, recent } = pick(found, q, part.unit, last);
    rows.push({ part, item, ...portionFor(item, part, last.get(item.i) ?? null, recent) });
  }
  return rows;
}

function portionFor(
  item: FoodItem,
  part: MealPart,
  prev: LastPortion | null,
  recent: boolean
): Pick<ReadRow, "qty" | "unit" | "how" | "unitSwap"> {
  const how = recent ? "recent" : "typed";
  if (part.count === null && part.unit === null) {
    if (prev && prev.qty > 0) return { qty: prev.qty, unit: prev.unit, how: "last" };
    return { qty: 1, unit: item.u, how: "serving" };
  }
  const count = part.count ?? 1;
  if (part.unit === "g") return { qty: count, unit: "g", how };
  if (part.unit === null || unitMatches(item, part.unit)) return { qty: count, unit: item.u, how };
  const alt = householdUnits(item).find((h) => h.unit === part.unit);
  if (alt) return { qty: count, unit: alt.unit, how };
  // The unit typed is not one this row is measured in, and there is no honest conversion from a
  // katori of one food to grams of another. The count is kept in the row's own unit and the row
  // says so, before anything is logged.
  return { qty: count, unit: item.u, how, unitSwap: part.unit };
}
